import { useState } from 'react';

import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { AiQuestion } from '~api/ai/types';

import { BulkAddQuestionsEventAdapter } from '~analytics/adapters/BulkAddQuestionsEventAdapter';
import { toast } from '~shared/components/Toast';
import { useBulkQuestionsUrl } from '~teacher/components/QuizEditor/contexts/BulkQuestionsUrlProvider';
import { useBulkCreateQuestions } from '~teacher/hook/question/useBulkCreateQuestions';

interface UseAddAllQuestionsParams {
  quizId: number;
  position: number;
  questions: AiQuestion[] | undefined;
  addedQuestionIds: Set<string>;
  onQuestionAdded: (questionId: string) => void;
  onSuccess: () => void;
  onError: () => void;
  uiSource: 'ai_import' | 'ai_extract' | 'ai_generate';
}

export function useAddAllQuestions({
  quizId,
  position,
  questions,
  addedQuestionIds,
  onQuestionAdded,
  onSuccess,
  onError,
  uiSource,
}: UseAddAllQuestionsParams) {
  const bulkQuestionsUrl = useBulkQuestionsUrl();
  const bulkCreateQuestions = useBulkCreateQuestions({ url: bulkQuestionsUrl });
  const [isAddingAllQuestions, setIsAddingAllQuestions] = useState(false);

  const addAllQuestions = () => {
    if (!questions || questions.length === 0) {
      return;
    }

    const questionsToAdd = questions.filter(
      ({ questionId }) => !addedQuestionIds.has(questionId)
    );

    if (questionsToAdd.length === 0) {
      return;
    }

    const numQuestionsToAddOfType = new Map<QuestionTypes, number>([
      [QuestionTypes.MultipleChoice, 0],
      [QuestionTypes.TrueFalse, 0],
      [QuestionTypes.FreeResponse, 0],
    ]);

    setIsAddingAllQuestions(true);

    bulkCreateQuestions.mutate(
      {
        insertionOrder: position,
        quizId: quizId as any,
        questions: questionsToAdd.map(
          ({ questionType, questionText, answers, explanation }) => {
            numQuestionsToAddOfType.set(
              questionType,
              numQuestionsToAddOfType.get(questionType)! + 1
            );
            return {
              type: questionType,
              questionText: questionText,
              answers: answers,
              explanation: explanation,
              gradingWeight: 1,
            };
          }
        ),
      },
      {
        onSuccess: () => {
          // Mark questions as added after successful creation
          questionsToAdd.forEach(({ questionId }) => {
            onQuestionAdded(questionId);
          });

          // Analytics
          new BulkAddQuestionsEventAdapter({
            numQuestions: questionsToAdd.length,
            numMultipleChoiceQuestions: numQuestionsToAddOfType.get(
              QuestionTypes.MultipleChoice
            ),
            numTrueFalseQuestions: numQuestionsToAddOfType.get(
              QuestionTypes.TrueFalse
            ),
            numShortAnswerQuestions: numQuestionsToAddOfType.get(
              QuestionTypes.FreeResponse
            ),
            uiSource,
          }).sendAnalytics();

          onSuccess();
        },
        onError: () => {
          toast.critical('Something went wrong');
          setIsAddingAllQuestions(false);
          onError();
        },
      }
    );
  };

  return { addAllQuestions, isAddingAllQuestions };
}
